/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { useAtom } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';

import { Banner } from '@atlaskit/navigation-system';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import { getConfig } from '../../../config';
import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { forceLoggedOutViewAtom } from '../../state';

import { messages } from './messages';

const styles = cssMap({
	container: {
		width: '100%',
		height: '100%',
		backgroundColor: token('color.background.accent.purple.subtlest.hovered'),
		paddingInline: token('space.400'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.150'),
		'@media (max-width: 30rem)': {
			justifyContent: 'space-between',
		},
	},
	button: {
		display: 'flex',
		paddingBlock: token('space.100'),
		paddingInline: token('space.300'),
		justifyContent: 'center',
		alignItems: 'center',
		color: token('color.text'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.text'),
		backgroundColor: 'transparent',
		borderRadius: token('radius.small'),
		font: token('font.body'),
		fontFamily: token('font.family.code'),
		fontWeight: token('font.weight.semibold'),
		textAlign: 'center',
		whiteSpace: 'nowrap',
		'&:hover': {
			cursor: 'pointer',
		},
		'@media (max-width: 30rem)': {
			paddingBlock: token('space.050'),
			paddingInline: token('space.100'),
		},
	},
});

export function SwitchViewBanner(): JSX.Element | null {
	const { loading, error } = useAtlassianUserContext();
	const [forceLoggedOut, setForceLoggedOut] = useAtom(forceLoggedOutViewAtom);
	const { formatMessage } = useIntl();
	const isLoggedIn = !loading && !error;
	const isUserAllowedToSeeBanner =
		fg('twg_microsite_view_switcher') || getConfig().isLocalDev === true;

	if (!isUserAllowedToSeeBanner) {
		return null;
	}

	if (!isLoggedIn) {
		return null;
	}

	return (
		<Banner>
			<div css={styles.container}>
				<FormattedMessage {...messages.switchTo} />
				<button
					aria-label={
						forceLoggedOut
							? formatMessage(messages.loggedInAriaLabel)
							: formatMessage(messages.loggedOutAriaLabel)
					}
					css={styles.button}
					type="button"
					onClick={() => setForceLoggedOut((prev) => !prev)}
				>
					<FormattedMessage
						{...(forceLoggedOut ? messages.loggedInViewLabel : messages.loggedOutViewLabel)}
					/>
				</button>
			</div>
		</Banner>
	);
}
